import { cx } from "@shared/components/untitledui/util/cx"

// Beside ChoiceCardFace rather than in it: a module exporting a component may export nothing
// else, or Vite's Fast Refresh gives up on the whole file.

/*
 * Two controls use the face of a card, but they do different work, so they do not share one
 * style:
 *
 * - A ChoiceButtons card answers a question. A press does the action, and the view changes.
 *   No card keeps a state after the press. The card below the pointer is the only card with
 *   a cyan edge (per the Figma "Option" frame).
 * - A ChoiceCards card holds a value. The card that the user selects keeps the cyan edge and
 *   the lift until the user selects a different card.
 */

const CARD_FACE = "flex-1 rounded-xl bg-white text-left transition"

/**
 * A card that is itself the interactive element (ChoiceButtons' Button). A soft shadow lifts
 * it off the panel. The border is transparent and not absent, because a transparent border
 * keeps the width of the card constant when the cyan edge comes and goes.
 */
export function choiceCardClassName({ isFocusVisible }: { isFocusVisible: boolean }): string {
  return cx(
    CARD_FACE,
    "cursor-pointer border border-transparent p-4 shadow-md ring-inset",
    // The pointer gives the card a 2px cyan edge. The second pixel is an inset ring and not a
    // wider border, because a wider border moves the cards below it.
    "hover:border-cyan-600 hover:ring-1 hover:ring-cyan-600",
    // `:hover` is not applied when a card moves to a position below a pointer that does not
    // move. For that condition only, hover-below-pointer sets `data-pointer-within`.
    "data-pointer-within:border-cyan-600 data-pointer-within:ring-1 data-pointer-within:ring-cyan-600",
    isFocusVisible ? "outline-focus-ring outline-2 outline-offset-2" : "outline-hidden",
  )
}

/**
 * A card that is a shell around its interactive element (ChoiceCards' Radio, whose detail
 * sits beside the label). The focus ring rises to the shell so it wraps the whole card.
 */
export function choiceCardShellClassName(isSelected: boolean): string {
  return cx(
    CARD_FACE,
    "border outline-hidden has-data-focus-visible:outline-2 has-data-focus-visible:outline-offset-2 has-data-focus-visible:outline-focus-ring",
    // The selected card keeps the white background of the other cards. A 2px cyan edge and a
    // shadow show the selection. The second pixel is an inset ring and not a wider border,
    // because a wider border moves the cards below it.
    isSelected
      ? "border-cyan-600 shadow-lg ring-1 ring-cyan-600 ring-inset"
      : "border-gray-200 hover:border-gray-300",
  )
}
